import {
  type AccountListRowData,
  AccountStatus,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import {
  ChatStatus,
  type SalesPortalAccountListDataPaginatedQuery,
  type SalesPortalAccountListFilters,
} from '@/types';

type PaginatedAccountInfo =
  SalesPortalAccountListDataPaginatedQuery['getSalesPortalAccountListInfoPaginated']['accounts'][number];

const ACCOUNT_STATUS_TO_CHAT_STATUS: Record<AccountStatus, ChatStatus> = {
  [AccountStatus.ACTIVE]: ChatStatus.Active,
  [AccountStatus.NEW]: ChatStatus.New,
  [AccountStatus.PROSPECT]: ChatStatus.Prospect,
};

// Inactive chats are filtered out server-side and have no account-list status,
// so they are intentionally absent — callers guard on the undefined result.
export const CHAT_STATUS_TO_ACCOUNT_STATUS: Partial<
  Record<ChatStatus, AccountStatus>
> = {
  [ChatStatus.Active]: AccountStatus.ACTIVE,
  [ChatStatus.New]: AccountStatus.NEW,
  [ChatStatus.Prospect]: AccountStatus.PROSPECT,
};

export function buildAccountListServerFilters({
  searchText,
  selectedGroups,
  selectedStatuses,
}: {
  searchText: string;
  selectedGroups: Set<string>;
  selectedStatuses: Set<AccountStatus>;
}): SalesPortalAccountListFilters | undefined {
  const searchTerm = searchText.trim();
  if (!searchTerm && selectedGroups.size === 0 && selectedStatuses.size === 0) {
    return undefined;
  }

  const filters: SalesPortalAccountListFilters = {};
  if (selectedGroups.size > 0) {
    filters.customer_groups = [...selectedGroups];
  }
  if (searchTerm) {
    filters.search_term = searchTerm;
  }
  if (selectedStatuses.size > 0) {
    filters.status = [...selectedStatuses].map(
      (status) => ACCOUNT_STATUS_TO_CHAT_STATUS[status],
    );
  }
  return filters;
}

export function transformToAccountListRowDataPaginated(
  accountInfoList: PaginatedAccountInfo[],
): AccountListRowData[] {
  return accountInfoList.map(
    (info): AccountListRowData => ({
      accountId: info.restaurant_account_id ?? null,
      lastFulfillmentDate: info.last_fulfilled_date_display
        ? { display: info.last_fulfilled_date_display }
        : null,
      restaurantAddress: info.restaurant_address,
      restaurantName: info.restaurant_name,
      restaurantUUID: info.restaurant_id,
      supplierUUID: info.supplier_id,
    }),
  );
}
